import { Node, mergeAttributes } from '@tiptap/core';
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import MathNodeView from './MathNodeView.vue';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        math: {
            /** Insert an inline math expression at the current selection. */
            insertMathInline: (options?: { latex?: string }) => ReturnType;
            /** Insert a display (block) math expression at the current position. */
            insertMathBlock: (options?: { latex?: string }) => ReturnType;
        };
    }
}

// ── Inline math ───────────────────────────────────────────────────────

export const MathInlineExtension = Node.create({
    name: 'mathInline',

    group: 'inline',

    inline: true,

    atom: true,

    addAttributes() {
        return {
            latex: {
                default: '',
                parseHTML: (el) => el.getAttribute('data-latex') ?? '',
                renderHTML: (attrs) => ({ 'data-latex': attrs.latex }),
            },
        };
    },

    parseHTML() {
        return [{ tag: 'span[data-math-inline]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(HTMLAttributes, { 'data-math-inline': '' }),
            0,
        ];
    },

    addNodeView() {
        return VueNodeViewRenderer(MathNodeView);
    },

    addCommands() {
        return {
            insertMathInline:
                ({ latex = '' } = {}) =>
                ({ commands }) =>
                    commands.insertContent({
                        type: 'mathInline',
                        attrs: { latex },
                    }),
        };
    },
});

// ── Display (block) math ──────────────────────────────────────────────

export const MathBlockExtension = Node.create({
    name: 'mathBlock',

    group: 'block',

    atom: true,

    draggable: true,

    addAttributes() {
        return {
            latex: {
                default: '',
                parseHTML: (el) => el.getAttribute('data-latex') ?? '',
                renderHTML: (attrs) => ({ 'data-latex': attrs.latex }),
            },
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-math-block]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, { 'data-math-block': '' }),
            0,
        ];
    },

    addNodeView() {
        return VueNodeViewRenderer(MathNodeView);
    },

    addCommands() {
        return {
            insertMathBlock:
                ({ latex = '' } = {}) =>
                ({ commands }) =>
                    commands.insertContent({
                        type: 'mathBlock',
                        attrs: { latex },
                    }),
        };
    },
});
